// Version 3: Streamlined Post-Transaction Check for Robocalling
// Updated December 2024 with new script requirements from Updated_PTC Call Script (AG)
// This version is for non-vulnerable clients and focuses on core compliance
// Default policy type: ILP (Investment-Linked Policy)

import { PS_CONFIG } from '@/types/ps-common';

export interface SurveyQuestion {
  id: string;
  question: string;
  questionZh?: string;
  probingQuestions?: string[];
  probingQuestionsZh?: string[];
  followUp?: {
    condition: string;
    question: string;
    questionZh?: string;
  };
  followUpQuestions?: {
    condition: string;
    questions: string[];
    questionsZh?: string[];
  };
  actions?: {
    [key: string]: string;
  };
  required: boolean;
  category: 'introduction' | 'verification' | 'survey' | 'closing';
}

export const PS_SURVEY_FLOW_V3: SurveyQuestion[] = [
  // Introduction and Verification (Section A)
  {
    id: 'intro_greeting',
    question: 'Good Morning/Afternoon/Evening, may I speak to Mr. Wong please?',
    questionZh: '早上/下午好，请问是黄先生吗？',
    category: 'introduction',
    required: true,
  },
  {
    id: 'intro_caller',
    question: `My name is ${PS_CONFIG.nameV3}, I'm calling on behalf of Prudential Singapore.`,
    questionZh: `您好，我是${PS_CONFIG.nameV3}，代表保诚保险新加坡公司与你联络。`,
    category: 'introduction',
    required: true,
  },
  {
    id: 'intro_consent',
    question: 'This call is regarding your signing of policy(s) from Prudential, and if you have a few minutes to spare, we would like to hear from the quality of the service that our FC has provided.',
    questionZh: '这通电话是关于您和保诚签订的保单。为了提高我们的服务素质，我们想了解您从您的财务顾问购买保单的过程与服务体验。请问可以抽出您几分钟的时间，做这项问卷吗？',
    category: 'introduction',
    required: true,
  },
  {
    id: 'verification_dob',
    question: 'Thank you. For verification purpose, may I have your full date of birth please?',
    questionZh: '谢谢。为了确认您的身份，请告诉我你的出生日期吗?',
    category: 'verification',
    required: true,
  },
  {
    id: 'verification_postal',
    question: 'May I have your postal code please?',
    questionZh: '请告诉我您的邮政号码。',
    followUp: {
      condition: 'if_questioned',
      question: 'As I might be disclosing some of your policy details, I will need to ensure that I am speaking with the right person.',
      questionZh: '由于我可能透露您一些保单细节，我需要确保我正在与适合的人对话。',
    },
    category: 'verification',
    required: true,
  },
  {
    id: 'recording_notice',
    question: 'Thank you Mr. Wong. Before I proceed, I wish to inform you that this call is recorded for quality and training purposes.',
    questionZh: '为了提升我们的服务素质，这通电话将会被记录，以便培训用途。',
    category: 'introduction',
    required: true,
  },

  // Survey Questions (Section B) - Updated from PTC Call Script (AG)
  {
    id: 'q1_documents',
    question: 'Have you received a set of documents that you signed at the point of sale? This could be either email or paper copy.',
    questionZh: '在销售过程后，您是否收到了您的保单文件？这可以通过电子邮件或纸副本。',
    probingQuestions: [
      'Do you recall receiving a set of documents that you had signed on an iPad at the point of sale, for example: PruPlanner, Policy Illustration, Product summary, Your Guide to Life Insurance? For ILP: Fund information Booklet, Product Highlight Sheets. This could be in your junk mail.',
      'Could I do a quick verification of your email address?',
      'Just wanted to inform you that since your email is correct, the documents should have been emailed to you already, or in a few hours time.',
      'If you still don\'t receive it in your email, please contact your Financial Consultant.',
    ],
    probingQuestionsZh: [
      '您还记得收到一份您在销售过程时所签署的文件吗？例如：个人财务策划专案，利益说明书，产品概要，人寿保险指南，基金信息手册，产品特色表，基金简报，基金说明书，基金产品介绍清单，年度报告。这可能在您的垃圾邮件里。',
      '请问我可以确认一下您的电子邮件地址吗？',
      '好的，既然您的电子邮件是正确的，您应该收到您的文件了，或在几个小时之内收到。',
      '如果您几个小时之后还没收到您的文件，您可以通知您的财务顾问。',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q2_needs_assessment',
    question: 'Before recommending this product, did your financial consultant ask you questions to understand your needs, financial situation, investment objectives and risk appetite?',
    questionZh: '在推荐此产品之前，您的财务顾问是否向您提问，以了解您的需求，财务状况，投资目标和您对投资风险的偏好？',
    probingQuestions: [
      'Did your financial consultant ask any questions about your annual income, expenses, budget and existing insurance policies?',
      'Did you share with your FC on the purpose of the purchase of the plan? Example saving or protection needs?',
      'Did you share the amount you want to set aside?',
      'Did you share your income/expenses?',
      'Did you share your existing investments or insurance plans that you have?',
    ],
    probingQuestionsZh: [
      '您的财务顾问是否询问有关您的年收入，开支，预算和现有保单的任何问题？',
      '您是否有和您的财务顾问分享买这份保单的用意？例如储蓄或者保险',
      '您是否有分享每年放多少钱？',
      '您是否有分享收入和开支？',
      '您是否有分享现有的保单？',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q3_accuracy',
    question: 'Was the information provided by you about your financial situation and financial needs documented accurately in the sales documents by your financial consultant?',
    questionZh: '您的财务顾问是否准确记录了您所提供的财务状况和需求的资料？',
    probingQuestions: [
      'Information such as: Income, expenses, amount of savings, purpose of the purchase of policy (savings or protection), premium and policy term, existing policies?',
    ],
    probingQuestionsZh: [
      '您是否有和您的财务顾问分享：收入、开支、多少储蓄、买这份保单的用意（例如储蓄或者保险）、每年放多少钱和放多少年、现有的保单？',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q4_understanding',
    question: "Did you understand your financial consultant's explanation on how the recommended policy is suitable for you?",
    questionZh: '您是否了解您的财务顾问对这份保单的解说，并了解这份保单如何适合您的需求？',
    category: 'survey',
    required: true,
  },
  // Q5: ILP (Investment-Linked Policy) - Single question with 5 sub-parts (a-e)
  // NOTE: This is ONE question asked in parts, not separate questions
  {
    id: 'q5_ilp',
    question: 'In making the product recommendation to you, did your financial consultant explain the...',
    questionZh: '在向您推荐产品时，您的财务顾问是否有向您解释...',
    // Sub-parts asked sequentially as part of Q5
    probingQuestions: [
      // Part a) Features and benefits
      'a) Features and benefits? For example, the policy has a protection element and Cash Values are non-guaranteed.',
      'The Investment-linked policy has coverage for death and the surrender value will depend on the performance of the funds you have purchased. Are you aware of this?',
      'Are you aware that return of the investment may be less or more than the amount you put in?',
      // Part b) Fees and charges
      'b) Fees and Charges? For example, Fund Management Fees.',
      'Are you aware the Fund Management Fees are paid with the units in your policy?',
      // Part c) Terms of the policy
      'c) Terms of the policy? For example, Premium Payment Term and Policy Term.',
      'Based on the information documented, may I confirm that you are aware that your policy premium payment term is X years and policy term is Y years?',
      // Part d) Investment Risk
      'd) Investment Risk? For example, Risk Classification of the funds you have purchased and Price of the units depend on how the funds perform.',
      'Based on the information documented, can I confirm that the funds you purchased are <Refer to PruPlanner> and the Risk Classification is <Refer to PruPlanner>?',
      // Part e) CPF Interest Rate (ILP CPF only)
      'e) CPF Interest Rate? (This is applicable for ILP CPF only)',
      'If you were aware of the prevailing CPF interest rates, would you still proceed to purchase the policy?',
    ],
    probingQuestionsZh: [
      // Part a) 特点和好处
      'a) 特点和好处？例如保单包含保险的部分和现金价值无保证',
      '您是否知道您购买的投资连结保单含有死亡保障和退保价值将取决于您购买的基金的表现而定？',
      '您是否知道保单的投资回报可能会比您投资的钱少？',
      // Part b) 费用和收费
      'b) 费用和收费？例如基金管理费',
      '您是否知道基金管理费是从您的保单里的基金单位扣除？',
      // Part c) 保单条款
      'c) 保单条款？例如保费付款期，保单期限',
      '根据记录，您是否知道的保费付款期是X年而保期是Y年？',
      // Part d) 投资风险
      'd) 投资风险？例如，您购买的基金风险分类和基金单位价格取决于基金表现',
      '根据记录，您所购买的基金是<参考PruPlanner>并且风险分类是<参考PruPlanner>？',
      // Part e) 公积金利率 (仅适用于公积金投资连结保单)
      'e) 公积金利率？（这仅适用于公积金投资连结保单）',
      '如果您知道目前的公积金利率，您还会继续购买这份保单吗？',
    ],
    category: 'survey',
    required: true,
  },
  {
    id: 'q6_freelook',
    question: 'Did your financial consultant inform you about the 14-day free look period?',
    questionZh: '您的财务顾问是否有向您解释有关保单14天免费冷静期的权益？',
    probingQuestions: [
      'Are you aware that you have 14 days (upon receiving your policy documents) to review your policy to see if it is suitable for your needs (to review your purchase decision or make changes)?',
      'Kindly note that upon receiving your policy documents, you would have 14 days to review your policy and make changes if required. At the same time, can I reconfirm that your Financial Consultant didn\'t inform you about the 14 days free look period?',
      'Would you require your financial consultant to revisit you and clarify your doubts?',
    ],
    probingQuestionsZh: [
      '您是否知道您有14天（在收到保单文件后）审查您的保单是否适合您的需求（考虑您的购买决定或进行更改）？',
      '好的。在我们继续之前想通知您在收到保单文件后，您有14天可以对您的保单进行更改。我能否再次确认您的财务顾问没有向您解释有关保单14天免费冷静期的权益？',
      '请问您需不需要你的财务顾问向您解说吗？',
    ],
    followUp: {
      condition: 'if_unaware',
      question: 'Kindly note that upon receiving your policy documents, you would have 14 days to review your policy and make changes if required. At the same time, can I reconfirm that your Financial Consultant didn\'t inform you about the 14-day free look period?',
      questionZh: '好的。在我们继续之前想通知您在收到保单文件后，您有14天可以对您的保单进行更改。我能否再次确认您的财务顾问没有向您解释有关保单14天免费冷静期的权益？',
    },
    category: 'survey',
    required: true,
  },
  {
    id: 'q7_replacement',
    question: 'Was the product purchased meant to replace any of your existing policy(ies)?',
    questionZh: '您所购买的保单是否是为了取代您现有的任何保单?',
    probingQuestions: [
      'Did you cancel or stop paying any policies to buy this current policy?',
    ],
    probingQuestionsZh: [
      '您是否取消或停止支付任何保单来购买此当前保单？',
    ],
    // CRITICAL: If YES, ALL THREE follow-up questions MUST be asked
    // For iii: If client is aware - AC; If not fully aware but replacement by 3rd party - AC; If not aware and FC recommended - proceed to ask for FC callback
    followUpQuestions: {
      condition: 'if_yes',
      questions: [
        'Could you share with me the reason for replacing your existing policy(ies)?',
        'Were you advised by anyone to replace or switch? If so, who was it?',
        'Are you aware that when you cancel any existing policy, you will lose the coverage, and/or your surrender value payable may be less than the total premiums paid?',
        'Can we get your financial consultant to get in touch with you and explain these terms again?',
        'Can I confirm you would like to proceed with the recommended plan without having your financial consultant explaining these terms again?',
      ],
      questionsZh: [
        '您能否与我们分享更换现有保单的原因吗？',
        '是否有任何人建议您更换现有保单？如果有，请问是谁？',
        '您是否知道，当您取消任何现有保单时，您将失去承保范围，和/或您的退保价值可能比已缴付总保费少？',
        '我们能否找财务顾问向您进一步解释？',
        '我能否确认您不需要财务顾问再向您解释并决定继续购买这份保单?',
      ],
    },
    category: 'survey',
    required: true,
  },
  {
    id: 'q8_feedback',
    question: 'Were there any aspects of the sales process or the policy that you would like to provide feedback on for our improvement?',
    questionZh: '关于您购买的保单已及销售过程，您是否有任何的建议或意见，可供我们参考并改善？',
    // Sentiment-based responses handled in prompt
    category: 'survey',
    required: true,
  },

  // Closing (Section C)
  {
    id: 'closing',
    question: 'Mr. Wong, we have come to the end of the survey. Thank you for taking the time to speak to me. Should you need any assistance from us, please contact your financial consultant or our Customer Service Hotline at 1800-333-0-333. Thank you once again and have a nice day.',
    questionZh: '先生/女士，谢谢您抽出宝贵的时间做这项问卷。如果您有任何询问，请联系您的财务顾问或我们的客户服务热线 1800-333-0-333。再次感谢您，祝您有个美好的一天。',
    category: 'closing',
    required: true,
  },
];

// Common Scenario Responses (Section D) - MUST use exact responses from Updated PTC Call Script (AG)
export const COMMON_SCENARIOS_V3 = {
  // Enquiries
  referToFC: {
    en: "My apologies, however, as I am only conducting the survey, I am afraid I do not have access to the relevant information in order to give you an accurate answer regarding your query. Would you need your FC to get back to you to clarify your doubts?",
    zh: "很抱歉，可是我只是在做这项电话问卷，所以我没有相关的资料准确地回答您。您需不需要您的财务顾问跟你解说吗？",
  },
  voucherQuestion: {
    en: "May I know what kind of voucher did your Financial Consultant tell you about? I'm sorry, I'm afraid that this survey does not entail any rewards of vouchers.",
    zh: "请问您的财务顾问告诉了您什么样的礼券？很抱歉，但是没有礼券可供您完成这项电话问卷。",
  },
  pruActiveCash: {
    en: "Your Yearly Cash Benefits can be withdrawn after the second year from the inception date of your PruActive Cash policy.",
    zh: "您能在保险单开始之日的第二年之后，开始领取您的年度现金利益。",
  },
  // Reactions
  endSurveyMidway: {
    en: "Thank you for taking the time to speak to me. Should you need any assistance from us, please contact your financial consultant or our Customer Service Hotline (at 1800-333-0-333). Thank you once again and have a nice day.",
    zh: "谢谢您抽出宝贵的时间做这项问卷。如果您有任何询问，请联系您的财务顾问或我们的客户服务热线 1800-333-0-333。再次感谢您，祝您有个美好的一天。",
  },
  clientAffected: {
    en: "My apologies, regarding your enquiries, may I transfer you to a senior customer service personnel to help you?",
    zh: "很抱歉，关于您的询问，我会把这通电话交给我的同事来为您解说",
  },
  positiveFeedback: {
    en: "I understand, thank you for your valuable feedback, may I ensure you that we would convey it to our staff and your Financial Consultant.",
    zh: "我明白，我们珍惜您的意见。谢谢。",
  },
  negativeFeedback: {
    en: "I'm sorry to hear that you had a negative experience with Prudential. May we ensure you that your feedback would be conveyed to our Financial Consultant. We would reflect on your feedback, and we hope to improve the quality of our customer service.",
    zh: "很抱歉您对保诚保险公司有负面的服务体验。我们一定会把您意见传达给我们财务顾问，以便提高我们的服务素质。",
  },
  // Introduction scenarios
  alreadyHasAgent: {
    en: "Understand that you already have an appointed agent, which is why I am calling to understand about your customer experience.",
    zh: "我们了解您有一位指定的财务顾问，这通电话是为了了解您的客户体验。",
  },
  alreadyBoughtPolicy: {
    en: "I understand that you have already bought a policy, however, kindly note that this is a survey and not a sales call and I am calling to understand about your customer experience.",
    zh: "我们了解您已经买了保单。我们与您确保这不是一个销售电话，我们是想跟您做一项电话问卷，并了解您的客户体验。",
  },
  scamCallConcern: {
    en: "I understand that you have bought a policy recently, and would like to call you to understand about your customer experience.",
    zh: "我们了解您已经跟财务顾问买了保单，这通电话是为了了解您的客户体验。",
  },
  clientBusy: {
    en: "May I know when would be a good time to call you back again?",
    zh: "那请问，什么时间方便联络您呢？",
  },
  clientBusyLunch: {
    en: "I understand that you are busy right now, would lunch be a good time to call you back?",
    zh: "好的，那请问我是否能在午餐时间打给您吗？",
  },
  wrongNumber: {
    en: "May I know whether is this the correct phone number? Thank you, sorry to trouble you.",
    zh: "请问你的电话号码是不是正确的？谢谢，抱歉打扰您。",
  },
  personNotClient: {
    en: "May I know when is a convenient time to speak with the policyholder?",
    zh: "那请问，什么时间方便联络保单持有人呢？",
  },
  policySpecificQuestion: {
    en: "My apologies, as a survey representative, I don't have access to your specific policy details. Would you like your Financial Consultant to contact you?",
    zh: "很抱歉，作为问卷调查员，我没有您保单的具体资料。您需要您的财务顾问联系您来解答吗？",
  },
  alreadyReceivedCall: {
    en: "Understand that you have previously received a call from us, however, kindly note that this is a survey and not a sales call and I am calling to understand about your customer experience. May I proceed with this survey?",
    zh: "我们了解您之前收到我们的电话。我们与您确保这不是一个销售电话，我们是想跟您做一项问卷，并了解您的客户体验。请问可以进行这项问卷吗？",
  },
};

// Agent's speaking patterns for natural conversation (V3 - Alex persona)
export const AGENT_SPEECH_PATTERNS_V3 = {
  fillers: ['Alright', 'Okay', 'I see', 'Right'],
  acknowledgments: ['Thank you', 'Thank you for that', 'I understand', 'Got it', 'Noted'],
  transitions: ['Now', 'Moving on', 'My next question is', 'Let me ask you'],
  politeness: ['Thank you for confirming', 'I appreciate that', 'Thanks for sharing'],
  clarifications: ['Let me clarify', 'Just to confirm', 'May I check'],
};

// Helper function to add natural speech patterns
export function addNaturalSpeech(text: string): string {
  const patterns = AGENT_SPEECH_PATTERNS_V3;

  // Randomly add acknowledgment at the beginning
  if (Math.random() < 0.3) {
    const ack = patterns.acknowledgments[Math.floor(Math.random() * patterns.acknowledgments.length)];
    text = `${ack}. ${text}`;
  }

  // Add transitions for follow-up questions
  if (text.toLowerCase().includes('next question') || Math.random() < 0.2) {
    const transition = patterns.transitions[Math.floor(Math.random() * patterns.transitions.length)];
    text = `${transition}, ${text}`;
  }

  return text;
}

// Function to get appropriate response based on client's answer
export function getAppropriateResponse(questionId: string, clientResponse: string): string {
  const lowerResponse = clientResponse.toLowerCase();

  // Positive responses
  if (lowerResponse.includes('yes') || lowerResponse.includes('correct') || lowerResponse.includes('received')) {
    const acknowledgments = [
      'Thank you for confirming.',
      'Great, thank you.',
      'Alright, noted.',
      'Perfect, thank you.',
    ];
    return acknowledgments[Math.floor(Math.random() * acknowledgments.length)];
  }

  // Negative or uncertain responses
  if (lowerResponse.includes('no') || lowerResponse.includes('not sure') || lowerResponse.includes("don't")) {
    return 'I understand. Let me make a note of that.';
  }

  // Default acknowledgment
  return 'Thank you for that information.';
}

// Detect if feedback is positive or negative
export function detectFeedbackSentiment(feedback: string): 'positive' | 'negative' | 'neutral' {
  const lowerFeedback = feedback.toLowerCase();

  const positiveKeywords = [
    'good', 'great', 'excellent', 'wonderful', 'helpful', 'professional', 'satisfied', 'happy', 'thank',
    '好', '很好', '满意', '专业', '感谢', '谢谢', '棒'
  ];

  const negativeKeywords = [
    'bad', 'poor', 'terrible', 'unhappy', 'disappointed', 'problem', 'issue', 'complaint', 'not satisfied',
    '不好', '差', '不满意', '问题', '投诉', '失望'
  ];

  const hasPositive = positiveKeywords.some(keyword => lowerFeedback.includes(keyword));
  const hasNegative = negativeKeywords.some(keyword => lowerFeedback.includes(keyword));

  if (hasNegative) return 'negative';
  if (hasPositive) return 'positive';
  return 'neutral';
}
